import React, { useRef, useEffect, useState } from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';
import { createRoot } from 'react-dom/client';
import MapMenu from './MapMenu'; // Updated import
import { VehicleMapIcon, TargetPositionMarker, HomePositionMarker, createOrUpdateLine, updateTrajectoryLayer, getVehicleMaterialColor } from './MapIcons';
import SetPositionDialog from './Map/SetPositionDialog';

const MAX_TRAJECTORY_POINTS = 1000;
const TRAJECTORY_UPDATE_INTERVAL = 30; //ms 
const trajectoryStoreTimestamp = {};

// const Default_Z_Index = 1000;
// const Default_Z_IndexSelected = 1001;
// const Default_Z_IndexTrajectory = 999;
// const Default_Z_IndexTrajectorySelected = 1000;
const Default_Z_IndexHome = 100;
const Default_Z_IndexHomeSelected = 102;
const Default_Z_IndexTarget = 100;
const Default_Z_IndexTargetSelected = 102;
//
const Default_Z_IndexVehicle = 120;
const Default_Z_IndexVehicleSelected = 121;

const Map = ({ selectedMavId, handleMavIdSelect, mavIdList, mavTimestamp }) => {
    const [centerSet, setCenterSet] = useState(false);
    const [mapMenu, setMapMenu] = useState(null);
    const [mapLoaded, setMapLoaded] = useState(false);
    const [trajectory, setTrajectory] = useState({});
    const [waypointDialogOpen, setWayPointDialogOpen] = useState(false);
    const [roiDialogOpen, setRoiDialogOpen] = useState(false);
    const [homeDialogOpen, setHomeDialogOpen] = useState(false);
    const [mapSelectedLocation, setMapSelectedLocation] = useState([null, null]);

    const mapRef = useRef();
    const mapContainerRef = useRef();
    //Vehicle markers
    const vehicleMarkersRef = useRef({});
    const vehicleRootsRef = useRef({});
    //Target position markers
    const targetPositionMarkersRef = useRef({});
    const targetLinesRef = useRef({}); // Add ref for target lines
    //Home position markers
    const homePositionMarkersRef = useRef({});
    //
    const DefaultCenter = [-74.5, 40];
    const DefaultZoomLevel = 15;
    const DefaultStyle = 'mapbox://styles/mapbox/satellite-v9';


    useEffect(() => {
        mapboxgl.accessToken = 'pk.eyJ1IjoidmltZHJvbmVzIiwiYSI6ImNtNTM4dms2eTJkMWkyanNmY2ZhcnIzZGcifQ.z0IO_XMqTufYeAdf1HpjWw';
        const map = new mapboxgl.Map({
            container: mapContainerRef.current,
            style: DefaultStyle, // Map style
            center: DefaultCenter, // Starting position [lng, lat]
            zoom: DefaultZoomLevel // Starting zoom level
        });


        map.on('load', () => {
            setMapLoaded(true);
        });

        map.on('mousedown', (e) => {
            const {button} = e.originalEvent;
            if (button === 2) { // Right-click
                e.preventDefault();
                setMapMenu({
                    x: e.originalEvent.clientX,
                    y: e.originalEvent.clientY,
                    lngLat: e.lngLat,
                });
            } else if (button === 0) { // Left-click
                setMapMenu(null); // Hide the map menu on left click
            }
        });

        mapRef.current = map;
        return () => {
            map.remove();
        };
    }, []);

    useEffect(() => {
        if (!mapLoaded) return;
        const mav = window.mav; // Access mav from window object
        if (!mav) return; // Ensure mav is defined

        const mavSystems = Object.keys(mav);
        mavSystems.forEach((mavId, index) => {
            const vehicle = mav[mavId];
            if (vehicle.GPS_RAW_INT === undefined || vehicle.ATTITUDE === undefined || vehicle.HEARTBEAT === undefined) {
                return;
            }
            const latitude = vehicle.GPS_RAW_INT.lat / 1e7;
            const longitude = vehicle.GPS_RAW_INT.lon / 1e7;
            const yaw = vehicle.ATTITUDE.yaw;

            const mavTypeName = mavIdList[mavId];
            const materialColor = getVehicleMaterialColor(index);

            if (!centerSet && latitude !== undefined && longitude !== undefined) {
                mapRef.current.setCenter([longitude, latitude]);
                setCenterSet(true);
            }

            const markerId = `marker-${mavId}`;

            let vehicleMapIcon = <VehicleMapIcon index={index} yaw={yaw} mavId={mavId} mavTypeName={mavTypeName} selectedMavId={selectedMavId} handleMavIdSelect={handleMavIdSelect} />

            if (!vehicleMarkersRef.current[markerId]) {
                // Create a new marker if it doesn't exist
                const vehicleMarkerElement = document.createElement('div');
                if (selectedMavId === mavId) {
                    vehicleMarkerElement.style.zIndex = Default_Z_IndexVehicleSelected;
                } else {
                    vehicleMarkerElement.style.zIndex = Default_Z_IndexVehicle;
                }
                const root = createRoot(vehicleMarkerElement);
                root.render(vehicleMapIcon);

                const marker = new mapboxgl.Marker(vehicleMarkerElement)
                    .setLngLat([longitude, latitude]) // Set the initial position of the vehicle marker
                    .addTo(mapRef.current);

                vehicleMarkersRef.current[markerId] = marker;
                vehicleRootsRef.current[markerId] = root;
            } else {
                // Update the position and rotation of the existing marker
                vehicleMarkersRef.current[markerId].setLngLat([longitude, latitude]);
                const root = vehicleRootsRef.current[markerId];
                root.render(vehicleMapIcon);
                const vehicleMarkerElement = vehicleMarkersRef.current[markerId].getElement();
                if (selectedMavId === mavId) {
                    vehicleMarkerElement.style.zIndex = Default_Z_IndexVehicleSelected;
                } else {
                    vehicleMarkerElement.style.zIndex = Default_Z_IndexVehicle;
                }
            }

            if (trajectoryStoreTimestamp[mavId] === undefined) {
                trajectoryStoreTimestamp[mavId] = new Date();
            }

            if (new Date() - trajectoryStoreTimestamp[mavId] > TRAJECTORY_UPDATE_INTERVAL) {
                // Update trajectory
                setTrajectory((prevTrajectory) => {
                    const newTrajectory = { ...prevTrajectory };
                    if (!newTrajectory[mavId]) {
                        newTrajectory[mavId] =  {coordinates: [], index: index};
                    }
                    newTrajectory[mavId].coordinates.push([longitude, latitude]);
                    if (newTrajectory[mavId].coordinates.length > MAX_TRAJECTORY_POINTS) {
                        newTrajectory[mavId].coordinates.shift(); // Remove the oldest point if more than 50
                    }
                    return newTrajectory;
                });
                trajectoryStoreTimestamp[mavId] = new Date();
            }

            if (vehicle.HOME_POSITION !== undefined) {
                const homeLatitude = vehicle.HOME_POSITION.latitude / 1e7;
                const homeLongitude = vehicle.HOME_POSITION.longitude / 1e7;
                const homePositionMarkerId = `home-position-marker-${mavId}`;

                if (!homePositionMarkersRef.current[homePositionMarkerId]) {
                    const homePositionMarkerElement = new mapboxgl.Marker(new HomePositionMarker(index).getElement())
                        .setLngLat([homeLongitude, homeLatitude])
                        .addTo(mapRef.current);
                    if (selectedMavId === mavId) {
                        homePositionMarkerElement.getElement().style.zIndex = Default_Z_IndexHomeSelected;
                    } else {
                        homePositionMarkerElement.getElement().style.zIndex = Default_Z_IndexHome;
                    };

                    homePositionMarkersRef.current[homePositionMarkerId] = homePositionMarkerElement;
                } else {
                    // Update the position of the existing home marker
                    homePositionMarkersRef.current[homePositionMarkerId].setLngLat([homeLongitude, homeLatitude]);
                    const homePositionMarkerElement = homePositionMarkersRef.current[homePositionMarkerId].getElement();
                    if (selectedMavId === mavId) {
                        homePositionMarkerElement.style.zIndex = Default_Z_IndexHomeSelected;
                    } else {
                        homePositionMarkerElement.style.zIndex = Default_Z_IndexHome;
                    };
                };
            };

            if (vehicle.POSITION_TARGET_GLOBAL_INT !== undefined) {
                const targetLatitude = vehicle.POSITION_TARGET_GLOBAL_INT.lat_int / 1e7;
                const targetLongitude = vehicle.POSITION_TARGET_GLOBAL_INT.lon_int / 1e7;
                const targetPositionMarkerId = `target-position-marker-${mavId}`;
                if (!targetPositionMarkersRef.current[targetPositionMarkerId]) {
                    const targetPositionMarkerElement = new mapboxgl.Marker(new TargetPositionMarker(index))
                        .setLngLat([targetLongitude, targetLatitude])
                        .addTo(mapRef.current);

                    if (selectedMavId === mavId) {
                        targetPositionMarkerElement.getElement().style.zIndex = Default_Z_IndexTargetSelected;
                    } else {
                        targetPositionMarkerElement.getElement().style.zIndex = Default_Z_IndexTarget;
                    }

                    targetPositionMarkersRef.current[targetPositionMarkerId] = targetPositionMarkerElement;
                } else {
                    // Update the position of the existing target marker
                    targetPositionMarkersRef.current[targetPositionMarkerId].setLngLat([targetLongitude, targetLatitude]);
                    const targetPositionMarkerElement = targetPositionMarkersRef.current[targetPositionMarkerId].getElement();
                    if (selectedMavId === mavId) {
                        targetPositionMarkerElement.style.zIndex = Default_Z_IndexTargetSelected;
                    } else {
                        targetPositionMarkerElement.style.zIndex = Default_Z_IndexTarget;
                    }
                }

                const targetPositionLineId = `target-position-line-${mavId}`;
                const lineCoordinates = [
                    [longitude, latitude],
                    [targetLongitude, targetLatitude]
                ];
                createOrUpdateLine(mapRef.current, targetPositionLineId, lineCoordinates);
            }
        });

        // Remove markers that are no longer in the mav array
        Object.keys(vehicleMarkersRef.current).forEach(markerId => {
            if (!mav[markerId.split('-')[1]]) {
                vehicleMarkersRef.current[markerId].remove();
                delete vehicleMarkersRef.current[markerId];
                delete vehicleRootsRef.current[markerId];
            }
        });

        // Remove target position markers and lines that are no longer in the mav array
        Object.keys(targetPositionMarkersRef.current).forEach(markerId => {
            if (!mav[markerId.split('-')[3]]) {  // Extract mavId from 'target-position-marker-{mavId}'
                targetPositionMarkersRef.current[markerId].remove();
                delete targetPositionMarkersRef.current[markerId];
            }
        });

        Object.keys(targetLinesRef.current).forEach(lineId => {
            if (!mav[lineId.split('-')[3]]) {  // Extract mavId from 'target-position-line-{mavId}'
                if (mapRef.current.getLayer(lineId)) {
                    mapRef.current.removeLayer(lineId);
                }
                if (mapRef.current.getSource(lineId)) {
                    mapRef.current.removeSource(lineId);
                }
                delete targetLinesRef.current[lineId];
            }
        });
    }, [mavTimestamp, centerSet]);

    useEffect(() => {
        if (!mapLoaded) return;

        Object.keys(trajectory).forEach((mavId) => {
            updateTrajectoryLayer(mapRef.current, trajectory, mavId);
        });
    }, [trajectory, mapLoaded]);

    const handleOptionSelect = (option) => {
        switch (option.value) {
            case 'go_to':
                setMapSelectedLocation([mapMenu.lngLat.lat, mapMenu.lngLat.lng]);
                setWayPointDialogOpen(true);
                break;
            case 'set_home':
                setMapSelectedLocation([mapMenu.lngLat.lat, mapMenu.lngLat.lng]);
                setHomeDialogOpen(true);
                break;
            case 'set_roi':
                setMapSelectedLocation([mapMenu.lngLat.lat, mapMenu.lngLat.lng]);
                setRoiDialogOpen(true);
                break;
            default:
                break;
        }
        setMapMenu(null); // Close the map menu after selecting an option
    };

    return (
        <div id="map" ref={mapContainerRef} onContextMenu={(e) => e.preventDefault()} style={{ width: '100%', height: '100%', position: 'relative' }}>
            {mapMenu && (
                <MapMenu
                    x={mapMenu.x}
                    y={mapMenu.y}
                    options={[
                        { label: 'Go To Location', value: 'go_to' },
                        { label: 'Set ROI', value: 'set_roi' },
                        { label: 'Set Home', value: 'set_home' },
                    ]}
                    onOptionSelect={handleOptionSelect}
                    open={Boolean(mapMenu)}
                    onClose={() => setMapMenu(null)}
                />
            )}
            <SetPositionDialog
                cmd='go_to'
                mapSelectedLocation={mapSelectedLocation}
                selectedMavId={selectedMavId}
                open={waypointDialogOpen}
                onClose={() => {
                    setWayPointDialogOpen(false);
                    setMapMenu(null);
                    setMapSelectedLocation([null, null]);
                }}
            />
            <SetPositionDialog
                cmd='set_roi'
                mapSelectedLocation={mapSelectedLocation}
                selectedMavId={selectedMavId}
                open={roiDialogOpen}
                onClose={() => {
                    setRoiDialogOpen(false);
                    setMapMenu(null);
                    setMapSelectedLocation([null, null]);
                }}
            />
            <SetPositionDialog
                cmd='set_home'
                mapSelectedLocation={mapSelectedLocation}
                selectedMavId={selectedMavId}
                open={homeDialogOpen}
                onClose={() => {
                    setHomeDialogOpen(false);
                    setMapMenu(null);
                    setMapSelectedLocation([null, null]);
                }}
            />
        </div>
    );
};

export default Map;